import React, { useRef, useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';
import type { GridReadyEvent, RowNode, ColDef } from 'ag-grid-community';
import type { AgGridReact as AgGridReactType } from 'ag-grid-react';
import { Button, Pagination, Row, Space, Spin, Empty } from 'antd';
import styles from './SimpleAgGrid.module.css';
import { debounce } from 'lodash';

interface SimpleAgGridProps<T extends Record<string, any>> {
  page?: any;
  dataSource?: any
  className?: any
  column: any;
  rowSelection?: any;
  rowKey?: any;
  onRowDoubleClick?: (event: any) => void;
  onCellClicked?: (event: any) => void;
  pageSizeOptions?: any
  defaultPageSize?: number
  showQuickJumper?: boolean
  showSizeChanger?: boolean
  pagination?: boolean;
  pinnedBottomRowData?: T[];
  getRowStyle?: (params: { node: RowNode }) => React.CSSProperties;
  onChangePage?: (current: number, size?: number) => void
  onSelectionChange?: (selectedRows: T[], selectedNodes: RowNode[]) => void; // 新增选择变化回调
  rowHeight?: number;
  headerHeight?: number;
  isLoading?: boolean;
  suppressRowClickSelection?: boolean;
  pageKey?: string;
  onColumnResized?: (event: any) => void; // 新增列宽变化回调
  onColumnMoved?: (columnOrder: string[]) => void; // 新增列移动回调
}

const SimpleAgGrid = <T extends Record<string, any>>({
  page,
  dataSource,
  className,
  column,
  pageSizeOptions,
  rowSelection,
  rowKey,
  pinnedBottomRowData,
  onRowDoubleClick,
  onCellClicked,
  pagination = true,
  defaultPageSize,
  showQuickJumper = true,
  showSizeChanger = true,
  getRowStyle,
  onChangePage,
  onSelectionChange,
  headerHeight = 41,
  rowHeight = 41,
  isLoading = false,
  suppressRowClickSelection = false,
  pageKey = '',
  onColumnResized,
  onColumnMoved
}: SimpleAgGridProps<T>) => {
  const gridRef: any = useRef<AgGridReactType>(null);

  // 列宽变化状态
  const [resizedFlag, setResizedFlag] = useState(false);

  // 保存列顺序到
  const onDragStopped: any = (columnApi: any) => {
    console.log(resizedFlag,"resizedFlag")
    if(resizedFlag){
      return 
    }
    try {
      // 获取当前所有列的colId，按当前顺序组成数组
      const columnInfo = columnApi
        .getAllDisplayedColumns()
        .map((col: any) => col.getColId());
      onColumnMoved && onColumnMoved(columnInfo);
    } catch (error) {
      console.error('保存列顺序失败:', error);
    }
  };

  const defaultColDef: ColDef = {
    sortable: false, //禁用列排序
    filter: true, //启用列过滤
    resizable: true, //允许调整列宽
    suppressMenu: true, //控制拖动列换顺序
    cellClass: 'border-cell',
    enablePivot: true,
    headerClass: 'header-bg-color', //为表头添加 CSS 类
    suppressMovable: false, // 默认允许列移动
    flex: 1, // 设置列默认使用flex布局，使列可以自动扩展填充空间
    lockPinned: true, // 固定列不允许换位置
    minWidth: 60, // 设置列的最小宽度，防止过窄
    maxWidth: 1200, // 设置列的最大宽度，防止过宽
  };

  // 处理列配置，确保固定列不可移动
  const processedColumns = React.useMemo(() => {
    return column?.map((col: any) => {
      // 如果列是固定的，或者已经设置了suppressMovable: true，则确保它不可移动
      if (col.pinned || col.suppressMovable) {
        return {
          ...col,
          suppressMovable: true
        };
      }
      return col;
    });
  }, [column]);

  // 自定义无数据组件，使用Ant Design的Empty组件
  const CustomNoRowsOverlay = () => {
    return (
      <div style={{ padding: '40px 0' }}>
        <Empty description="暂无数据" />
      </div>
    );
  };

  return (
    <>
      <div className={styles.container} style={{ position: 'relative' }}>
        <Spin
          spinning={isLoading}
          style={{
            width: '100%',
            height: '100%',
            paddingTop: '15%',
            background: 'rgb(255, 255, 255,.65)',
            position: 'absolute',
            // top: '30%',
            // left: '50%',
            // right: '50%',
            zIndex: 99,
          }}></Spin>
        {/* 表格区域 - 添加ag-theme-alpine类名作为主题 */}
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={gridRef}
            domLayout="normal" // 恢复为normal以保持固定高度设置
            pagination={false}
            headerHeight={headerHeight} //头部高度
            rowHeight={rowHeight} //行高
            columnDefs={processedColumns} //页面列（已处理固定列不可移动）
            rowData={page ? page.items : dataSource} //页面数据
            rowSelection={rowSelection || 'none'} //复选框
            defaultColDef={defaultColDef} //默认配置
            pinnedBottomRowData={pinnedBottomRowData || []} //合计行
            onRowDoubleClicked={onRowDoubleClick} //双击事件
            getRowStyle={getRowStyle ? (params: any) => getRowStyle(params) as Record<string, any> : undefined}
            onSelectionChanged={(event) => {
              //复选框选择事件
              if (onSelectionChange) {
                const selectedNodes = event.api.getSelectedNodes();
                const selectedRows = selectedNodes.map((node) => node.data);
                onSelectionChange(selectedRows, selectedNodes);
              }
            }}
            suppressRowClickSelection={suppressRowClickSelection} // 禁用点击整行勾选checkbox功能
            onColumnVisible={(event) => {
              // 当列的可见性改变时，自动调整列宽以填充空间
              setTimeout(() => {
                event.api.sizeColumnsToFit();
              }, 0);
            }}
            
            // 禁止将列拖出表格区域来隐藏列
            suppressDragLeaveHidesColumns={true}
            // 使用自定义无数据组件
            noRowsOverlayComponent={CustomNoRowsOverlay}
            onColumnResized={(event: any) => {
              if(event.finished){
                setResizedFlag(true)
              }
            //   // 当列宽改变时，触发回调
            //   console.log('拖动列宽', event.finished)
            //   if (onColumnResized && !event.finished) {
            //     // 保存列宽到localStorage
            //     const columnId = event.column.getColId();
            //     const newWidth = event.column.getActualWidth();
            //     if (columnId && newWidth) {
            //       try {
            //         const savedWidths = JSON.parse(localStorage.getItem('AGColumnsWidth_' + pageKey) || '{}');
            //         savedWidths[columnId] = newWidth;
            //         console.log(savedWidths, 'savedWidths')
            //         localStorage.setItem('AGColumnsWidth_' + pageKey, JSON.stringify(savedWidths));
            //         onColumnResized(savedWidths)
            //       } catch (error) {
            //         console.error('保存列宽失败:', error);
            //       }
            //     }
            //   }
            }}
            // 处理列移动事件，保存列顺序
            onDragStopped={(event: any) => {
              setResizedFlag(false)
              onDragStopped(event.columnApi)
            }}
          />
        </div>
        {page && pagination !== false && (
          <Row
            justify='end'
            align='middle'
            style={{
              height: '60px',
              padding: '0 15px',
            }}
          >
            <Pagination
              defaultPageSize={defaultPageSize}
              showQuickJumper={showQuickJumper}
              showSizeChanger={showSizeChanger}
              pageSize={page.size < 0 ? 10 : page.size}
              current={page.current}
              total={page.total}
              pageSizeOptions={
                pageSizeOptions
                  ? pageSizeOptions
                  : ['10', '20', '50', '100', '500']
              }
              onChange={onChangePage}
              className={styles.pagination}
              showTotal={(total) => `共 ${total} 条`}
            />
          </Row>
        )}
      </div>
    </>
  );
};

export default SimpleAgGrid;